import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { GridApi, ColumnState, AgPublicEventType } from 'ag-grid-community';
import { isEqual } from 'lodash-es';
import { debounceTime, Observable, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, groupBy, map, mergeMap, takeUntil, tap } from 'rxjs/operators';

import { OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import {
	GetSessionApplicationPreferencesResponse,
	SessionApplicationPreferencesApiService
} from '@shure/cloud/shared/organizations/data-access';
import { ILogger } from '@shure/shared/angular/utils/logging';

export interface GridConfig {
	columnState: ColumnState[];
	rowGroupColumns: string[];
}

export interface GridConfigChangeEvent {
	componentId: string;
	configuration: GridConfig;
}

interface GridPreferences {
	componentId: string;
	defaultConfiguration: GridConfig;
	lastConfiguration?: GridConfig;
	isLoading: boolean;
	migrateConfiguration?: (configuration: GridConfig) => GridConfig;
	cancelLoad: Subject<void>;
	loadResult: Observable<boolean>;
	sessionRevision: number;
}

@Injectable({
	providedIn: 'root'
})
export class UserAgGridPreferencesService implements OnDestroy {
	private readonly preferencesApi = inject(SessionApplicationPreferencesApiService);
	private readonly oktaService = inject(OktaInterfaceService);
	private readonly logger = inject(ILogger);

	public gridPreferencesSaved = signal<GridConfigChangeEvent | undefined>(undefined);
	private configChange$ = new Subject<{ change: GridConfigChangeEvent; sessionRevision: number }>();

	// Debounce time for saving configurations
	private saveTimeout = 2000;
	private destroy$ = new Subject<void>();
	private readonly grids = new Map<GridApi, GridPreferences>();
	private readonly autoSaveCallbacks = new Map<GridApi, () => void>();
	private readonly gridsWithCleanup = new WeakSet<GridApi>();
	private readonly sessionChanged$ = new Subject<void>();
	private sessionKey: string | null = null;
	private sessionRevision = 0;

	// The ag-grid changes/events to monitor and to trigger auto saving.
	private autoSaveEvents: AgPublicEventType[] = [
		'columnMoved',
		'columnVisible',
		'columnPinned',
		'sortChanged',
		'columnRowGroupChanged'
	];

	constructor() {
		this.oktaService.$accessTokenClaims
			.pipe(
				map((claims) => {
					if (!claims) {
						return null;
					}
					const { sub, shure_tenant: organizationId, aud, cid } = claims;
					return sub && organizationId ? JSON.stringify([sub, organizationId, aud, cid]) : null;
				}),
				distinctUntilChanged(),
				takeUntil(this.destroy$)
			)
			.subscribe((sessionKey) => {
				this.sessionKey = sessionKey;
				this.sessionRevision++;
				this.sessionChanged$.next();
				this.gridPreferencesSaved.set(undefined);
				this.reloadOrganizationPreferences();
			});
		// Debounce each component independently so switching tables does not drop another table's save.
		this.configChange$
			.pipe(
				groupBy(({ change, sessionRevision }) => `${sessionRevision}:${change.componentId}`),
				mergeMap((componentChanges) => componentChanges.pipe(debounceTime(this.saveTimeout))),
				takeUntil(this.destroy$)
			)
			.subscribe(({ change, sessionRevision }) => {
				if (this.sessionKey && sessionRevision === this.sessionRevision) {
					this.saveUserSettings(change);
				}
			});
	}

	/**
	 * Sets up auto-save functionality for grid changes.
	 * @param gridApi The AG Grid API instance.
	 * @param saveCallback Callback function to trigger on auto-save events.
	 */
	public setupAutoSave(gridApi: GridApi, saveCallback: () => void): void {
		this.removeTrackedAutoSave(gridApi);
		this.registerGridCleanup(gridApi);
		this.autoSaveEvents.forEach((event) => gridApi?.addEventListener(event, saveCallback));
		this.autoSaveCallbacks.set(gridApi, saveCallback);
	}

	/**
	 * Removes the event listeners for the grid so the callback function
	 * will not be called. When removing listeners, it is important to use
	 * the same function reference that was used in the setup.
	 * @param gridApi The AG Grid API instance.
	 * @param saveCallback Callback associated with a prior call to setupAutoSave
	 */
	public removeAutoSave(gridApi: GridApi, saveCallback: () => void): void {
		this.autoSaveEvents.forEach((event) => gridApi?.removeEventListener(event, saveCallback));
		this.removeTrackedAutoSave(gridApi);
	}

	/**
	 * Loads the component configuration for the authenticated user and organization.
	 * @param gridApi The AG Grid API instance.
	 * @param componentId Unique key for identifying the grid configuration.
	 * @param gridConfigMigrator a function to call to merge a new Config into an existing one.
	 * 		By default ag-grid will append new columns to the end of the table, but that may
	 * 		not be the preferred location for a new column.
	 * @returns an observable that emits a true value when the loading is complete or an error occurred.
	 */
	public loadGridConfig(
		gridApi: GridApi,
		componentId: string,
		gridConfigMigrator?: (savedGridConfig: GridConfig) => GridConfig
	): Observable<boolean> {
		const existingPreferences = this.grids.get(gridApi);
		if (
			existingPreferences?.isLoading &&
			existingPreferences.componentId === componentId &&
			existingPreferences.sessionRevision === this.sessionRevision
		) {
			// The same table is already loading for this session, so reuse it instead of canceling and refetching.
			return existingPreferences.loadResult;
		}
		const retVal = new ReplaySubject<boolean>(1);
		existingPreferences?.cancelLoad.next();
		const preferences: GridPreferences = {
			componentId,
			defaultConfiguration: existingPreferences?.defaultConfiguration ?? this.captureGridConfig(gridApi),
			isLoading: true,
			migrateConfiguration: gridConfigMigrator,
			cancelLoad: new Subject<void>(),
			loadResult: retVal.asObservable(),
			sessionRevision: this.sessionRevision
		};
		this.grids.set(gridApi, preferences);
		this.registerGridCleanup(gridApi);
		if (!this.sessionKey) {
			retVal.next(true);
			retVal.complete();
			return preferences.loadResult;
		}
		this.preferencesApi
			.getSessionApplicationPreferences({ componentIds: this.getComponentId(componentId) })
			.pipe(
				takeUntil(this.destroy$),
				takeUntil(this.sessionChanged$),
				takeUntil(preferences.cancelLoad),
				tap((response) => this.applySavedConfiguration(gridApi, preferences, response))
			)
			.subscribe({
				complete: () => {
					this.finishLoading(gridApi, preferences, retVal);
				},
				error: (error: unknown) => {
					this.logger.error('Error loading grid configuration:', String(error));
					this.finishLoading(gridApi, preferences, retVal);
				}
			});
		return preferences.loadResult;
	}

	/**
	 * Saves the current grid configuration.
	 * @param gridApi The AG Grid API instance.
	 * @param componentId Unique key for identifying the grid configuration.
	 */
	public saveGridConfig(gridApi: GridApi, componentId: string): void {
		const preferences = this.grids.get(gridApi);
		if (!this.sessionKey || preferences?.isLoading || (preferences && preferences.componentId !== componentId)) {
			return; // Skip saving during config loading
		}
		const columnState = gridApi.getColumnState();
		const rowGroupColumns = gridApi.getRowGroupColumns().map((col) => col.getColId());

		const configuration: GridConfig = {
			columnState,
			rowGroupColumns
		};
		// Saving of the grid configuration.
		if (this.isGridConfigEqual(configuration, preferences?.lastConfiguration ?? null)) {
			return; // No changes detected, skip saving
		}
		if (preferences) {
			preferences.lastConfiguration = configuration;
		}
		this.configChange$.next({
			change: { componentId: this.getComponentId(componentId), configuration },
			sessionRevision: this.sessionRevision
		});
	}

	/**
	 * Cleanup resources on service destruction.
	 */
	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.grids.clear();
		this.autoSaveCallbacks.clear();
	}

	/**
	 * Saves the grid configuration for a component.
	 * @param body The payload containing the grid configuration.
	 */
	private saveUserSettings(body: GridConfigChangeEvent): void {
		this.preferencesApi
			.updateSessionApplicationPreference({
				body: { componentId: body.componentId, configuration: { ...body.configuration } }
			})
			.pipe(
				takeUntil(this.destroy$),
				takeUntil(this.sessionChanged$),
				tap(() => {
					this.gridPreferencesSaved.set(body);
				})
			)
			.subscribe({
				next: (response) => {
					this.logger.information('response', response.body.message);
				},
				error: (error) => {
					this.logger.error('Error saving grid configuration:', String(error));
				}
			});
	}

	private getComponentId(componentId: string): string {
		const preferenceComponentId = `shurecloud-${componentId}_table-config`;
		if (!componentId.trim() || preferenceComponentId.length > 100) {
			throw new Error('Grid preference component ID must be nonblank and at most 100 characters.');
		}
		return preferenceComponentId;
	}

	/** Keeps one active auto-save callback per grid when its component ID changes. */
	private removeTrackedAutoSave(gridApi: GridApi): void {
		const saveCallback = this.autoSaveCallbacks.get(gridApi);
		if (!saveCallback) {
			return;
		}
		if (!gridApi.isDestroyed()) {
			this.autoSaveEvents.forEach((event) => gridApi.removeEventListener(event, saveCallback));
		}
		this.autoSaveCallbacks.delete(gridApi);
	}

	/** Cancels loading and clears listeners when a grid is destroyed. */
	private registerGridCleanup(gridApi: GridApi): void {
		if (this.gridsWithCleanup.has(gridApi)) {
			return;
		}
		this.gridsWithCleanup.add(gridApi);
		gridApi.addEventListener('gridPreDestroyed', () => {
			this.grids.get(gridApi)?.cancelLoad.next();
			this.grids.delete(gridApi);
			this.autoSaveCallbacks.delete(gridApi);
		});
	}

	/** Applies only this table's object configuration, preserving the existing migration hook. */
	private applySavedConfiguration(
		gridApi: GridApi,
		preferences: GridPreferences,
		response: GetSessionApplicationPreferencesResponse
	): void {
		const savedPreference = response.body.find(
			(preference) => preference.componentId === this.getComponentId(preferences.componentId)
		);
		if (!savedPreference) {
			return;
		}
		const { columnState, rowGroupColumns } = savedPreference.configuration;
		if (!Array.isArray(columnState)) {
			throw new Error('Saved grid configuration must contain a columnState array.');
		}
		let configuration: GridConfig = {
			columnState,
			rowGroupColumns: Array.isArray(rowGroupColumns)
				? rowGroupColumns
				: columnState
						.filter((column: ColumnState) => column.rowGroup)
						.sort(
							(left: ColumnState, right: ColumnState) =>
								(left.rowGroupIndex ?? 0) - (right.rowGroupIndex ?? 0)
						)
						.map((column: ColumnState) => column.colId)
		};
		if (preferences.migrateConfiguration) {
			configuration = preferences.migrateConfiguration(configuration);
		}
		gridApi.applyColumnState({ state: configuration.columnState, applyOrder: true });
		gridApi.setRowGroupColumns(configuration.rowGroupColumns);
		gridApi.refreshHeader();
	}

	/** Records the initial state so load and failure fallback never auto-save defaults. */
	private finishLoading(gridApi: GridApi, preferences: GridPreferences, result: ReplaySubject<boolean>): void {
		preferences.lastConfiguration = this.captureGridConfig(gridApi);
		preferences.isLoading = false;
		result.next(true);
		result.complete();
	}

	/** Restores defaults before loading preferences with the new authenticated organization. */
	private reloadOrganizationPreferences(): void {
		this.grids.forEach((preferences, gridApi) => {
			if (gridApi.isDestroyed()) {
				return;
			}
			preferences.isLoading = true;
			gridApi.applyColumnState({ state: preferences.defaultConfiguration.columnState, applyOrder: true });
			gridApi.setRowGroupColumns(preferences.defaultConfiguration.rowGroupColumns);
			gridApi.setFilterModel(null);
			this.loadGridConfig(gridApi, preferences.componentId, preferences.migrateConfiguration);
		});
	}

	private captureGridConfig(gridApi: GridApi): GridConfig {
		return {
			columnState: gridApi.getColumnState().map((column) => ({ ...column })),
			rowGroupColumns: gridApi.getRowGroupColumns().map((column) => column.getColId())
		};
	}

	private isGridConfigEqual(config1: GridConfig | null, config2: GridConfig | null): boolean {
		if (!config1 || !config2) {
			return false; // If either config is null, they are not equal
		}

		return isEqual(config1, config2);
	}
}
